// Phase H+1 Mini-Wave 5 · V12 User Mega-menu "Vai a..." · overlay fuzzy.
// V12 Achromatic Strict · italiano puro default · fallback i18n.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, X } from "lucide-react";
import { motion } from "framer-motion";

import { V12_USER_NAV_GROUPS, filterNavByRole } from "@/data/v12_user_nav";
import { useTranslation } from "@/i18n/I18nContext";

function _navKey(id) { return `user_nav.${(id || "").replace(/-/g, "_")}`; }
function _label(t, item) {
    const v = t(_navKey(item.id));
    return (v && v !== _navKey(item.id)) ? v : item.label;
}
function _group(t, g) {
    const v = t(_navKey(g.id));
    return (v && v !== _navKey(g.id)) ? v : g.label;
}
function _norm(s) { return (s || "").toString().toLowerCase().trim(); }
function _score(item, query) {
    if (!query) return 0;
    const q = _norm(query);
    const h = _norm(`${item.label} ${item.groupLabel || ""} ${(item.keywords || []).join(" ")}`);
    if (h.includes(q)) return h.indexOf(q) === 0 ? 100 : 50;
    let qi = 0;
    for (let i = 0; i < h.length && qi < q.length; i++) if (h[i] === q[qi]) qi++;
    return qi === q.length ? 20 : 0;
}

export default function V12UserMegaMenu({ onClose, role = "user" }) {
    const [query, setQuery] = useState("");
    const inputRef = useRef(null);
    const { t } = useTranslation();
    useEffect(() => { inputRef.current?.focus(); }, []);

    const groups = useMemo(
        () => filterNavByRole(V12_USER_NAV_GROUPS, role),
        [role],
    );

    const filteredGroups = useMemo(() => {
        if (!query) return groups;
        return groups
            .map((g) => ({
                ...g,
                items: g.items
                    .map((it) => ({ ...it, groupLabel: g.label, _score: _score({ ...it, groupLabel: g.label }, query) }))
                    .filter((it) => it._score > 0),
            }))
            .filter((g) => g.items.length > 0);
    }, [query, groups]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm
                       flex items-start justify-center pt-16"
            onClick={onClose}
            data-testid="v12-user-mega-overlay"
        >
            <motion.div
                initial={{ y: -8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.22 }}
                className="w-full max-w-5xl mx-4 rounded-xl border v12-border
                           v12-bg shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="v12-user-mega-menu"
            >
                <div className="flex items-center gap-3 px-4 py-3 border-b v12-border">
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("user_nav.mega_search_placeholder") !== "user_nav.mega_search_placeholder"
                            ? t("user_nav.mega_search_placeholder")
                            : "Cerca sezioni, moduli, pagine..."}
                        className="flex-1 bg-transparent text-sm text-neutral-100
                                   placeholder:text-neutral-500 outline-none"
                        data-testid="v12-user-mega-input"
                    />
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-neutral-500 hover:text-neutral-100 transition-colors duration-200"
                        aria-label={t("common.close")}
                        data-testid="v12-user-mega-close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3
                               gap-6 p-5 max-h-[70vh] overflow-y-auto"
                    data-testid="v12-user-mega-grid"
                >
                    {filteredGroups.length === 0 && (
                        <div className="col-span-full text-xs text-neutral-500 py-6 text-center">
                            {t("user_nav.mega_no_results") !== "user_nav.mega_no_results"
                                ? t("user_nav.mega_no_results")
                                : `Nessun risultato per "${query}".`}
                        </div>
                    )}
                    {filteredGroups.map((g) => (
                        <div key={g.id} className="min-w-0" data-testid={`v12-user-mega-group-${g.id}`}>
                            <h3 className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">
                                {_group(t, g)}
                            </h3>
                            <ul className="space-y-0.5">
                                {g.items.map((it) => {
                                    const itemLabel = _label(t, it);
                                    if (!it.implemented) {
                                        return (
                                            <li key={it.id}>
                                                <div
                                                    className="flex items-center gap-2 px-2 py-1.5
                                                               rounded-md text-xs text-neutral-600
                                                               cursor-not-allowed"
                                                    title={t("user_nav.coming_soon") !== "user_nav.coming_soon"
                                                        ? t("user_nav.coming_soon") : "Prossimamente"}
                                                    data-testid={`v12-user-mega-item-${it.id}`}
                                                    data-implemented="false"
                                                >
                                                    <span className="truncate">{itemLabel}</span>
                                                    <Lock size={10} className="ml-auto stroke-zinc-700" />
                                                </div>
                                            </li>
                                        );
                                    }
                                    if (it.legacyHref) {
                                        return (
                                            <li key={it.id}>
                                                <a
                                                    href={it.legacyHref}
                                                    onClick={onClose}
                                                    className="flex items-center gap-2 px-2 py-1.5
                                                               rounded-md text-xs text-neutral-300
                                                               v12-hover
                                                               hover:text-neutral-100 transition-colors duration-200"
                                                    data-testid={`v12-user-mega-item-${it.id}`}
                                                    data-legacy="true"
                                                >
                                                    <span className="truncate">{itemLabel}</span>
                                                </a>
                                            </li>
                                        );
                                    }
                                    return (
                                        <li key={it.id}>
                                            <Link
                                                to={it.to}
                                                onClick={onClose}
                                                className="flex items-center gap-2 px-2 py-1.5
                                                           rounded-md text-xs text-neutral-300
                                                           v12-hover
                                                           hover:text-neutral-100 transition-colors duration-200"
                                                data-testid={`v12-user-mega-item-${it.id}`}
                                            >
                                                <span className="truncate">{itemLabel}</span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
                <div className="px-4 py-2 border-t v12-border
                                text-[11px] text-neutral-500 text-right">
                    Esc {t("user_nav.close_hint") !== "user_nav.close_hint"
                        ? t("user_nav.close_hint") : "per chiudere"}
                </div>
            </motion.div>
        </motion.div>
    );
}
